import { useMemo, useRef, useState } from 'react'
import { IconDownload, IconPrinter } from '@tabler/icons-react'
import { ColumnFiltersState } from '@tanstack/table-core'
import { useParams } from 'react-router-dom'
import { Button, Group, Stack, Switch, Title, Tooltip } from '@mantine/core'
import { BreedDropdown } from '@shared/components/breed-dropdown/breed-dropdown'
import { SeasonDropdown } from '@shared/components/season-dropdown/season-dropdown'
import { StockAgeDropdown } from '@shared/components/stock-age-dropdown/stock-age-dropdown'
import {
  LotDetailsTable,
  ResultOptions,
} from '@shared/components/wool-data/lot-details-table/lot-details-table'
import { GetLotDetailsGql } from '@/graphql/queries/get-lot-details.graphql'
import { generateCSV } from '@shared/helpers/generate-csv/generate-csv'
import { useAuthenticatedGraphQuery } from '@/hooks/use-authenticated-query'
import { Breed, LotDetail, StockAge, WoolSoldStatus } from '@loom-api-types'
import { convertLotDetailKeysToLabels } from '@shared/components/wool-data/lot-details-table/lot-details-labels'
import { LotDataPrintTable } from '@/components/stations/wool-data/lot-data-print-table/lot-data-print-table'
import { PrintOnly } from '@shared/components/print-only/print-only'
import { showErrorNotification } from '@shared/helpers/notifications/notifications'
import { useReactToPrint } from 'react-to-print'

const ResultOptionToWoolSoldStatus = {
  [ResultOptions.Sold]: WoolSoldStatus.SoldOrBin,
  [ResultOptions.Unsold]: WoolSoldStatus.Unsold,
}

export function LotDetails() {
  const [seasonStartDate, setSeasonStartDate] = useState<Date | undefined>(undefined)
  const [viewAllUnsoldLots, setViewAllUnsoldLots] = useState(false)
  const [breed, setBreed] = useState<Breed | null>()
  const [stockAge, setStockAge] = useState<StockAge | null>()

  // Get the station record number from the URL
  const { stationRecordNumber } = useParams()

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  // Determine what sold result status to filter by
  const soldResultToFilterBy: WoolSoldStatus | null = useMemo(() => {
    // Use the 'view all unsold lots' switch to determine if we should show all unsold lots
    if (viewAllUnsoldLots) {
      return WoolSoldStatus.Unsold
    }

    // Use the column filters to determine
    return ResultOptionToWoolSoldStatus[
      columnFilters.find((dict) => dict.id === 'result')?.value as ResultOptions
    ]
  }, [viewAllUnsoldLots, columnFilters])

  const enableSeasonFilter: boolean = useMemo(() => !viewAllUnsoldLots, [viewAllUnsoldLots])
  const seasonFilterValue: string | undefined = useMemo(() => {
    if (viewAllUnsoldLots) {
      return undefined
    }

    return seasonStartDate?.getFullYear().toString().substring(2)
  }, [viewAllUnsoldLots, seasonStartDate])

  // Get the lot details data
  const { data, isLoading, isError } = useAuthenticatedGraphQuery({
    queryKey: [
      'lotDetailsData',
      stationRecordNumber,
      columnFilters,
      seasonStartDate,
      breed,
      stockAge,
      seasonFilterValue,
      soldResultToFilterBy,
    ],
    gql: GetLotDetailsGql,
    queryParams: {
      stationRecordNumber,
      saleAndSeason: columnFilters.find((dict) => dict.id === 'saleAndSeason')?.value as string,
      season: seasonFilterValue,
      purchaseAccountName: columnFilters.find((dict) => dict.id === 'purchaseAccountName')
        ?.value as string,
      result: soldResultToFilterBy,
      breed,
      stockAge,
    },
    enabled: !!stationRecordNumber,
  })

  const getCsvFilename = () => `${stationRecordNumber}-lot-data.csv`

  const downloadCSV = () => {
    if (data?.listLotDetails?.results) {
      // Convert the response data to a new format that match the table columns
      const convertedResults = convertLotDetailKeysToLabels(
        data.listLotDetails.results as LotDetail[]
      )
      generateCSV(convertedResults, getCsvFilename())
    }
  }

  // Print table functionality
  const printComponentRef = useRef<HTMLDivElement>(null)
  const handlePrintButtonClick = useReactToPrint({
    contentRef: printComponentRef,
  })

  const printLotDetails = () => {
    try {
      handlePrintButtonClick()
    } catch (error) {
      showErrorNotification('Printing failed. Please try again.')
    }
  }

  const totals = data?.listLotDetails?.lotDetailTotals
  const averages = data?.listLotDetails?.lotDetailWeightedAverages

  return (
    <Stack>
      <Group>
        <Title order={2} data-testid='station-wool-data-lot-details' mr='auto'>
          Lot Data
        </Title>
        <Switch
          label='View all unsold lots'
          checked={viewAllUnsoldLots}
          onChange={(event) => setViewAllUnsoldLots(event.currentTarget.checked)}
        />
        <BreedDropdown setBreed={setBreed} />
        <StockAgeDropdown setStockAge={setStockAge} />
        <SeasonDropdown
          seasonsBeforeCurrent={5}
          seasonsAfterCurrent={1}
          setSeasonStartDate={(val) => {
            if (val instanceof Date && !isNaN(val.getTime())) {
              setSeasonStartDate(val)
            }
          }}
          setSeasonEndDate={() => undefined} // Season end date is currently not used by this page
          enabled={enableSeasonFilter}
        />
        <Tooltip label='Download raw data as CSV'>
          <Button onClick={downloadCSV} leftSection={<IconDownload />}>
            Download
          </Button>
        </Tooltip>
        <Tooltip label='Print lot data'>
          <Button
            onClick={printLotDetails}
            leftSection={<IconPrinter />}
            disabled={!data?.listLotDetails?.results}
          >
            Print Lot Data
          </Button>
        </Tooltip>
      </Group>
      <div data-testid='print-lot-data'>
        <PrintOnly ref={printComponentRef}>
          <LotDataPrintTable
            data={data?.listLotDetails?.results ?? []}
            isLoading={isLoading}
            isError={isError}
            sumOfTotalBales={totals?.bales ?? null}
            sumOfTotalNetKg={totals?.netKgs ?? null}
            weightedAverageOfColour={averages?.coreTestColourYZ ?? null}
            weightedAverageOfMicron={averages?.coreTestMicron ?? null}
            weightedAverageOfPointOfBreakBase={averages?.coreTestPointOfBreakBase ?? null}
            weightedAverageOfPointOfBreakMid={averages?.coreTestPointOfBreakMid ?? null}
            weightedAverageOfPointOfBreakTip={averages?.coreTestPointOfBreakTip ?? null}
            weightedAverageOfStapleAverageStrength={averages?.coreTestStapleAverageStrength ?? null}
            weightedAverageOfStapleLength={averages?.coreTestStapleLengthGreasy ?? null}
            weightedAverageOfStapleStrength={averages?.coreTestStapleStrength ?? null}
            weightedAverageOfVegetableMatter={averages?.coreTestVegetableMatterBase ?? null}
            weightedAverageOfYield={averages?.coreTestYield ?? null}
            season={viewAllUnsoldLots ? undefined : seasonStartDate?.getFullYear().toString()}
          />
        </PrintOnly>
      </div>
      <div data-testid='lot-details-table'>
        <LotDetailsTable
          data={data?.listLotDetails?.results ?? []}
          isLoading={isLoading}
          isError={isError}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          enableResultFilter={!viewAllUnsoldLots}
          sumOfTotalBales={totals?.bales ?? null}
          sumOfTotalGrossProceeds={totals?.grossProceeds ?? null}
          sumOfTotalNetKg={totals?.netKgs ?? null}
          weightedAverageOfAirflowMicron={averages?.coreTestMicronAirflow ?? null}
          weightedAverageOfCoefficientOfVariationLaserscan={
            averages?.coreTestCoefficientOfVariationLaserscan ?? null
          }
          weightedAverageOfColour={averages?.coreTestColourYZ ?? null}
          weightedAverageOfLaserscanMicron={averages?.coreTestMicronLaserscan ?? null}
          weightedAverageOfMicron={averages?.coreTestMicron ?? null}
          weightedAverageOfOfdaMicron={averages?.coreTestMicronOfda ?? null}
          weightedAverageOfPointOfBreakBase={averages?.coreTestPointOfBreakBase ?? null}
          weightedAverageOfPointOfBreakMid={averages?.coreTestPointOfBreakMid ?? null}
          weightedAverageOfPointOfBreakTip={averages?.coreTestPointOfBreakTip ?? null}
          weightedAverageOfPriceClean={averages?.priceClean ?? null}
          weightedAverageOfPriceGreasy={averages?.priceGreasy ?? null}
          weightedAverageOfStapleAverageStrength={averages?.coreTestStapleAverageStrength ?? null}
          weightedAverageOfStapleLength={averages?.coreTestStapleLengthGreasy ?? null}
          weightedAverageOfStapleStrength={averages?.coreTestStapleStrength ?? null}
          weightedAverageOfVegetableMatter={averages?.coreTestVegetableMatterBase ?? null}
          weightedAverageOfYield={averages?.coreTestYield ?? null}
        />
      </div>
    </Stack>
  )
}
